import {
  Close,
  ExpandMore,
  LocationOn,
} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slide,
  Typography,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import BarnHorsesAutocomplete from '../../../../../../../components/Autocompletes/BarnHorses';
import LocationAutocomplete from '../../../../../../../components/Autocompletes/Locations';
import Horse from '../../../../../../../components/Horse';
import LocationChip from '../../../../../../../components/LocationChip';
import {
  addHorseLocation,
  deleteHorseLocation,
  updateHorseLocation,
} from '../../../../../../../redux/horse/actions';
import { getAllHorseLocations } from '../../../../../../../redux/location/actions';
import { primary } from '../../../../../../../utils/brandColors';
import {
  fromPickerValue,
  toMomentValue,
} from '../../../../../../../utils/dateHelper';
import theme from '../../../../../../../utils/theme';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const AddEditModal = ({
  open,
  onClose,
  horseId,
  horseIds,
  horseLocation,
  location: propLocation,
  startDate: propStartDate,
  endDate: propEndDate,
}) => {
  const dispatch = useDispatch();
  const fullscreen = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useColorScheme();

  const allBarnHorses = useSelector((state) => state.barn.allBarnHorses);
  const horseLocationsByHorse = useSelector((state) => state.location.horseLocationsByHorse)
  const boardStartDate = useSelector((state) => state.board.startDate)
  const boardEndDate = useSelector((state) => state.board.endDate)

  // React state
  const [startDate, setStartDate] = React.useState(new Date());
  const [startDateValidation, setStartDateValidation] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [endDateValidation, setEndDateValidation] = React.useState(null);
  const [location, setLocation] = React.useState(null);
  const [locationValidation, setLocationValidation] = React.useState(null);
  const [savingError, setSavingError] = React.useState(null);
  const [barnHorsesToAdd, setBarnHorsesToAdd] = React.useState([]);
  const [horseLocationsToEnd, setHorseLocationsToEnd] = React.useState(null);
  const [horseLocationsToRestart, setHorseLocationsToRestart] = React.useState([]);

  // Redux state
  const { isFetchingHorseLocations } = useSelector((state) => state.horse);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);

  React.useEffect(() => {
    setStartDate(horseLocation
      ? new Date(horseLocation.startDate)
      : propStartDate || new Date());
    setStartDateValidation(null);
    setEndDate((horseLocation && horseLocation.endDate)
      ? new Date(horseLocation.endDate)
      : propEndDate || null);
    setEndDateValidation(null);

    setSavingError(null);
    setLocation(propLocation || null);
    setLocationValidation(null);
  }, [horseLocation, open]);

  React.useEffect(() => {
    const listOfHorses = horseId
      ? [horseId]
      : (horseIds || []);
    const finalList = []
    for (let i = 0; i < listOfHorses.length; i += 1) {
      if (barnHorsesMap[listOfHorses[i]] && barnHorsesMap[listOfHorses[i]][0]) {
        finalList.push(barnHorsesMap[listOfHorses[i]][0]);
      }
    }
    setBarnHorsesToAdd(finalList);
    setHorseLocationsToEnd(null);
    setHorseLocationsToRestart([])
  }, [horseId, horseIds])

  const validateFields = (isNewLocation) => {
    if (!startDate) {
      setStartDateValidation('Please provide a start date at this location.');
      return false;
    } setStartDateValidation(null);
    if (endDate && endDate.getTime() < startDate.getTime()) {
      setEndDateValidation('End date must be after start date.');
      return false;
    } setEndDateValidation(null);
    if (isNewLocation && !location) {
      setLocationValidation('Please select a location');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    const isNewHorseLocation = !horseLocation;
    if (!validateFields(isNewHorseLocation)) { return; }

    // Adding new location
    if (isNewHorseLocation) {
      if (barnHorsesToAdd.length > 0) {
        // multiple horses passed in so loop over call
        const promises = barnHorsesToAdd.map((bh) => bh.horse.id).map((id) => dispatch(addHorseLocation(id, location.id, {
          startDate,
          endDate,
        }, false)));
        if (horseLocationsToEnd && horseLocationsToEnd.length > 0) {
          promises.push(...horseLocationsToEnd.map((hlte) => dispatch(updateHorseLocation(
            hlte.horse.id,
            hlte.location.id,
            { endDate: startDate },
            false,
          ))));
        }
        if (horseLocationsToRestart && horseLocationsToRestart.length > 0) {
          promises.push(...horseLocationsToRestart.map((hltr) => dispatch(addHorseLocation(hltr.horse.id, hltr.location.id, {
            startDate: endDate,
            endDate: null,
          }, false))));
        }
        Promise.all(promises).then(() => {
          // true to refresh data of consumer since locations might have been added that were not returned, if continuing after new location ends
          onClose(true);
          // reload all horse locations
          dispatch(getAllHorseLocations(boardStartDate, boardEndDate));
        }).catch((error) => {
          setSavingError(error.message || 'There was an error attempting to add the horses to this location. Please contact Questri support.');
          console.error(error);
          // reload all horse locations
          dispatch(getAllHorseLocations(boardStartDate, boardEndDate));
        });
      } else {
        onClose(false);
      }
    } else {
      dispatch(updateHorseLocation(horseId, horseLocation.location.id, {
        startDate,
        endDate,
      }, new Date(horseLocation.startDate))).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the horse location. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleDelete = () => {
    dispatch(deleteHorseLocation(horseId, horseLocation.location.id, new Date(horseLocation.startDate))).then(() => {
      onClose();
    }).catch((error) => {
      setSavingError(error.message || 'There was an error attempting to delete the horse location. Please contact Questri support.');
      console.error(error);
    });
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      open={open}
      fullScreen={fullscreen}
      onClose={onClose}
      PaperProps={{
        sx: {
          m: 0,
          mx: fullscreen
            ? 0
            : 2,
          width: '100%',
          maxWidth: 900,
        },
      }}
      aria-labelledby="modal-add-horse-title"
      aria-describedby="modal-add-horse-description"
    >
      <DialogTitle
        sx={{
          background: mode === 'light'
            ? primary.main
            : 'var(--template-palette-background-paper)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          id="modal-add-horse-title"
          variant="h4"
          component="span"
          color="#fff"
        >
          {horseLocation
            ? 'Edit'
            : 'Add'} Horse Location {horseLocation && `(${horseLocation.location.name})`}
        </Typography>
        <IconButton onClick={onClose}><Close sx={{ color: '#fff' }} /></IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ p: 3 }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >

              {
                horseId && barnHorsesMap[horseId]
                && (
                  <Box
                    sx={{ my: 2 }}
                  ><Horse horse={barnHorsesMap[horseId][0].horse} />
                  </Box>
                )
              }
              {
                horseIds && !horseId && (
                  <BarnHorsesAutocomplete
                    multiple
                    value={barnHorsesToAdd}
                    onSelect={(e, newHorses) => {
                      setBarnHorsesToAdd(newHorses);
                    }}
                    limitTags={8}
                    size="small"
                    barnHorses={allBarnHorses}
                    label="Horses to add to location"
                  />
                )
              }
            </Box>
          </Grid>
          <Grid size={12}>
            {
              // only show if adding new location
              !horseLocation
                ? (
                  <LocationAutocomplete
                    onSelect={(e, value) => { setLocation(value); }}
                    value={location}
                    validation={locationValidation}
                    multiple={false}
                    label="Location to add horses to"
                  />
                )
                : <LocationChip location={horseLocation.location} />
            }
          </Grid>

          <Grid>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Start Date"
                value={toMomentValue(startDate)}
                maxDate={toMomentValue(endDate || undefined)}
                onChange={(newValue) => {
                  setStartDate(fromPickerValue(newValue));
                }}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    error: !!startDateValidation,
                    helperText: startDateValidation,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="End Date"
                value={toMomentValue(endDate)}
                minDate={toMomentValue(startDate)}
                onChange={(newValue) => {
                  setEndDate(fromPickerValue(newValue));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!endDateValidation,
                    helperText: endDateValidation
                      || 'After this date, the sharing link will no longer work. If left blank, access never ends.',
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={12}>
            {
              !horseLocation && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >

                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontSize: '1rem' }}
                    gutterBottom
                  >
                    Do you want to end the current location for the horse(s) when this new location starts?
                  </Typography>
                  <IconButton
                    onClick={() => setHorseLocationsToEnd(!horseLocationsToEnd
                      ? []
                      : null)}
                  ><ExpandMore />
                  </IconButton>
                </Box>
              )
            }
            <Collapse in={!!(horseLocationsToEnd && !horseLocation)}>
              <List
                sx={{
                  width: '100%',
                  maxHeight: 250,
                  height: 250,
                  overflow: 'auto',
                }}
              >
                {barnHorsesToAdd.filter((bh) => horseLocationsByHorse[bh.horse.id]).map((bh) => {
                  const labelId = `checkbox-list-secondary-label-${bh.id}`;
                  return (
                    <ListItem
                      divider
                      key={bh.id}
                      disablePadding
                    >
                      <Box
                        sx={{
                          flex: 1,
                          mt: 1,
                        }}
                      >
                        <Horse horse={bh.horse} size={32} />
                        <List
                          dense
                          sx={{
                            width: '100%',
                            py: 0,
                          }}
                        >
                          {
                            horseLocationsByHorse[bh.horse.id].map((hl) => (
                              <ListItem
                                key={hl.id}
                                disablePadding
                                secondaryAction={(
                                  <Checkbox
                                    edge="end"
                                    onChange={(e) => {
                                      setHorseLocationsToEnd(e.target.checked
                                        ? [...horseLocationsToEnd, hl]
                                        : horseLocationsToEnd.filter((hlte) => hlte.id !== hl.id));
                                      if (!e.target.checked) {
                                        setHorseLocationsToRestart(horseLocationsToRestart.filter((hltr) => hltr.id !== hl.id));
                                      }
                                    }}
                                    checked={horseLocationsToEnd && !!horseLocationsToEnd.find((hlte) => hlte.id === hl.id)}
                                    inputProps={{ 'aria-labelledby': labelId }}
                                  />
                                )}
                              >
                                <ListItemIcon>
                                  <LocationOn />
                                </ListItemIcon>
                                <Box sx={{ flex: 1 }}>

                                  <ListItemText
                                    primary={hl.location.name}
                                    secondaryTypographyProps={{ component: 'div' }}
                                    secondary={(
                                      <Box>
                                        <div>{`${moment(hl.startDate).format('ll')} - ${!hl.endDate
                                          ? 'No End Date'
                                          : moment(hl.endDate).format('ll')}`}
                                        </div>

                                        {

                                          !!endDate && horseLocationsToEnd && horseLocationsToEnd.find((hlte) => hlte.id === hl.id) && (
                                            <FormControlLabel
                                              control={(
                                                <Checkbox
                                                  onChange={(e) => {
                                                    setHorseLocationsToRestart(e.target.checked
                                                      ? [...horseLocationsToRestart, hl]
                                                      : horseLocationsToRestart.filter((hltr) => hltr.id !== hl.id));
                                                  }}
                                                  checked={!!horseLocationsToRestart.find((hltr) => hltr.id === hl.id)}
                                                />
                                              )}
                                              label="Restart at this location after end date"
                                            />
                                          )
                                        }
                                      </Box>
                                    )}
                                  />
                                </Box>
                              </ListItem>
                            ))
                          }
                        </List>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Grid>
        </Grid>
        <Divider
          sx={{ my: 2 }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        {!horseLocation && !savingError && <Box />}
        {horseLocation && <Button onClick={handleDelete} endIcon={<DeleteIcon />} disabled={isFetchingHorseLocations}>Delete</Button>}
        {savingError && <Typography variant="caption" component="div" color="error" gutterBottom>{savingError}</Typography>}
        <Button onClick={handleSave} disabled={isFetchingHorseLocations} variant="contained" color="primary" loading={isFetchingHorseLocations} loadingPosition="start" startIcon={<SaveIcon />}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

AddEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  horseId: PropTypes.string,
  horseIds: PropTypes.array,
  horseLocation: PropTypes.object,
  location: PropTypes.object,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
};

AddEditModal.defaultProps = {
  horseLocation: null,
  horseId: null,
  horseIds: [],
  location: null,
  startDate: null,
  endDate: null,
};

export default AddEditModal;
